/**
 * The counter's dialogs: record a payment, adjust the bill, close the session, open a session. Each keeps what
 * the cashier typed when the backend refuses, shows the refusal in words, and never submits twice at once.
 * Errors stay inside the dialog (the entry is still there to correct); the bill behind it is reread.
 */
import { useId, useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Money } from '@/components/Money';
import { Sheet } from '@/components/Sheet';
import { Spinner } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { formatMoney } from '@/lib/money';
import { isApiError } from '@/api/errors';
import type { AdjustmentType, Bill, FloorLocation } from './api';
import { describeAmountProblem, minorToInput, parseAmountInput } from './amount';
import { COUNTER_CURRENCY } from './config';
import { asMoney, Notice, pendingCount, useChangedSince } from './parts';
import {
  useAcceptedTenders,
  useAddAdjustment,
  useCloseSession,
  useIntentKey,
  useOpenSession,
  useRecordPayment,
} from './queries';
import { describeCounterError, locationText, tenderLabel } from './wording';
import { cardClasses } from '@/components/Card';

const fmt = (minor: number) => formatMoney(asMoney(minor));

/** Refusals that are about the amount typed, so they are shown under the amount field, not at the bottom. */
const AMOUNT_CODES = [
  'PAYMENT_EXCEEDS_BALANCE',
  'BILL_ALREADY_SETTLED',
  'INVALID_ADJUSTMENT',
  'BILL_BELOW_SETTLED',
];
const isAmountRefusal = (error: unknown) =>
  isApiError(error) && AMOUNT_CODES.some((code) => error.hasCode(code));

function Choice({
  name,
  value,
  checked,
  onChange,
  children,
}: {
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  children: string;
}) {
  return (
    <label
      className={cn(
        'flex min-h-12 cursor-pointer items-center gap-2 rounded-control border px-4',
        checked ? 'border-primary bg-primary-soft' : 'border-field bg-white',
      )}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        className="size-4 accent-primary"
      />
      <span className="text-base font-medium text-ink">{children}</span>
    </label>
  );
}

// ─── Record a payment ────────────────────────────────────────────────────────

export function PaymentSheet({ bill, onClose }: { bill: Bill; onClose: () => void }) {
  const group = useId();
  const tenders = useAcceptedTenders(bill.bill_id, true);
  const record = useRecordPayment();
  const intent = useIntentKey();
  const changed = useChangedSince(bill.remaining_balance);
  const [tender, setTender] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [attempted, setAttempted] = useState(false);

  const methods = tenders.data ?? [];
  const chosen = tender ?? (methods.length === 1 ? methods[0] : null);
  const parsed = parseAmountInput(amount, COUNTER_CURRENCY);
  const amountError =
    attempted && !parsed.ok ? describeAmountProblem(parsed.problem, COUNTER_CURRENCY) : undefined;

  function submit(event: FormEvent) {
    event.preventDefault();
    setAttempted(true);
    if (!parsed.ok || !chosen || record.isPending) return;
    const note = reference.trim();
    const signature = [bill.bill_id, chosen, parsed.minor, note].join('|');
    record.mutate(
      {
        billId: bill.bill_id,
        input: {
          tender_type: chosen,
          amount: parsed.minor,
          ...(note ? { reference_notes: note } : {}),
        },
        idempotencyKey: intent.keyFor(signature),
      },
      {
        onSuccess: () => {
          intent.reset();
          onClose();
        },
      },
    );
  }

  const failure = record.error ? describeCounterError(record.error, 'payment', fmt) : null;
  const amountFailure = failure && isAmountRefusal(record.error) ? failure : null;

  return (
    <Sheet open onClose={onClose} title="Record payment">
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <div className="rounded-surface bg-attention-soft p-3">
          <p className="text-sm font-semibold text-attention-ink">Balance due</p>
          <p className="text-2xl font-semibold text-attention-ink tabular-nums">
            <Money value={asMoney(bill.remaining_balance)} />
          </p>
        </div>
        {changed && (
          <Notice tone="warn">
            The balance due changed while this window was open. Check the amount before recording.
          </Notice>
        )}

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-medium text-ink">Payment method</legend>
          {tenders.isPending && (
            <p role="status" className="flex items-center gap-2 text-sm text-muted">
              <Spinner size={16} /> Loading payment methods…
            </p>
          )}
          {tenders.isError && (
            <Notice
              tone="error"
              action={
                <Button variant="secondary" onClick={() => void tenders.refetch()}>
                  Try again
                </Button>
              }
            >
              Couldn&apos;t load the payment methods, so no payment can be recorded yet.
            </Notice>
          )}
          {tenders.isSuccess && methods.length === 0 && (
            <Notice tone="warn">
              This branch has no payment methods set up for the counter. Ask a manager.
            </Notice>
          )}
          {methods.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {methods.map((code) => (
                <Choice
                  key={code}
                  name={group}
                  value={code}
                  checked={chosen === code}
                  onChange={() => setTender(code)}
                >
                  {tenderLabel(code)}
                </Choice>
              ))}
            </div>
          )}
          {attempted && !chosen && methods.length > 0 && (
            <p className="text-xs font-medium text-danger">Choose a payment method.</p>
          )}
        </fieldset>

        <div className="flex flex-col gap-2">
          <Input
            label="Amount received"
            inputMode="decimal"
            autoComplete="off"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            error={amountError ?? (amountFailure && <span role="alert">{amountFailure}</span>)}
            hint="In rupees. It can't be more than the balance due."
          />
          <Button
            variant="secondary"
            className="self-start"
            onClick={() => setAmount(minorToInput(bill.remaining_balance, COUNTER_CURRENCY))}
          >
            Fill balance due
          </Button>
        </div>

        <Input
          label="Reference (optional)"
          value={reference}
          maxLength={255}
          onChange={(event) => setReference(event.target.value)}
          autoComplete="off"
        />

        {failure && !amountFailure && (
          <p role="alert" className="text-sm font-medium text-danger">
            {failure}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={record.isPending}
            disabled={tenders.isPending || tenders.isError || methods.length === 0}
          >
            Record payment
          </Button>
        </div>
      </form>
    </Sheet>
  );
}

// ─── Adjust the bill (manager) ───────────────────────────────────────────────

const ADJUSTMENT_CHOICES: { value: AdjustmentType; label: string }[] = [
  { value: 'AUTHORIZED_DISCOUNT', label: 'Discount' },
  { value: 'COMPLIMENTARY_DISCOUNT', label: 'Complimentary' },
];

export function AdjustmentSheet({ bill, onClose }: { bill: Bill; onClose: () => void }) {
  const group = useId();
  const adjust = useAddAdjustment();
  const intent = useIntentKey();
  const changed = useChangedSince(bill.total_amount);
  const [type, setType] = useState<AdjustmentType>('AUTHORIZED_DISCOUNT');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [attempted, setAttempted] = useState(false);

  const parsed = parseAmountInput(amount, COUNTER_CURRENCY);
  const amountError =
    attempted && !parsed.ok ? describeAmountProblem(parsed.problem, COUNTER_CURRENCY) : undefined;
  const reasonError = attempted && reason.trim() === '' ? 'Give a reason.' : undefined;
  const failure = adjust.error ? describeCounterError(adjust.error, 'adjustment', fmt) : null;
  const amountFailure = failure && isAmountRefusal(adjust.error) ? failure : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    setAttempted(true);
    const code = reason.trim();
    if (!parsed.ok || code === '' || adjust.isPending) return;
    const note = notes.trim();
    adjust.mutate(
      {
        billId: bill.bill_id,
        input: { type, amount: parsed.minor, reason_code: code, ...(note ? { notes: note } : {}) },
        idempotencyKey: intent.keyFor([bill.bill_id, type, parsed.minor, code, note].join('|')),
      },
      {
        onSuccess: () => {
          intent.reset();
          onClose();
        },
      },
    );
  }

  return (
    <Sheet open onClose={onClose} title="Adjust bill">
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <div className="rounded-surface bg-surface p-3">
          <p className="text-sm font-medium text-muted">Current total</p>
          <p className="text-2xl font-semibold text-ink tabular-nums">
            <Money value={asMoney(bill.total_amount)} />
          </p>
        </div>
        {changed && (
          <Notice tone="warn">
            The bill changed while this window was open. Check it before applying.
          </Notice>
        )}
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-medium text-ink">Type</legend>
          <div className="flex flex-wrap gap-2">
            {ADJUSTMENT_CHOICES.map((choice) => (
              <Choice
                key={choice.value}
                name={group}
                value={choice.value}
                checked={type === choice.value}
                onChange={() => setType(choice.value)}
              >
                {choice.label}
              </Choice>
            ))}
          </div>
        </fieldset>
        <Input
          label="Amount to take off"
          inputMode="decimal"
          autoComplete="off"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          error={amountError ?? (amountFailure && <span role="alert">{amountFailure}</span>)}
          hint="In rupees. The new total is shown after it is applied."
        />
        <Input
          label="Reason"
          value={reason}
          maxLength={50}
          onChange={(event) => setReason(event.target.value)}
          error={reasonError}
          autoComplete="off"
        />
        <Input
          label="Notes (optional)"
          value={notes}
          maxLength={200}
          onChange={(event) => setNotes(event.target.value)}
          autoComplete="off"
        />
        <p className="text-sm text-muted">
          This is recorded against your name and can&apos;t be edited or deleted.
        </p>
        {failure && !amountFailure && (
          <p role="alert" className="text-sm font-medium text-danger">
            {failure}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={adjust.isPending}>
            Apply adjustment
          </Button>
        </div>
      </form>
    </Sheet>
  );
}

// ─── Close the session ───────────────────────────────────────────────────────

/** Refusals that a branch manager may override with a reason (the backend decides; this only reveals the field). */
const OVERRIDABLE = [
  'BILL_NOT_SETTLED',
  'SESSION_HAS_UNSERVED_ITEMS',
  'SESSION_HAS_PENDING_PAYMENTS',
];

export function CloseSheet({
  sessionId,
  where,
  bill,
  canForce,
  onClosed,
  onClose,
}: {
  sessionId: string;
  where: string;
  bill: Bill | null;
  canForce: boolean;
  onClosed: () => void;
  onClose: () => void;
}) {
  const close = useCloseSession();
  const [reason, setReason] = useState('');
  const [attempted, setAttempted] = useState(false);
  const error = close.error;
  const overridable =
    canForce && isApiError(error) && OVERRIDABLE.some((code) => error.hasCode(code));
  const pending = bill ? pendingCount(bill) : 0;

  function run(force: boolean) {
    if (close.isPending) return;
    if (force) {
      setAttempted(true);
      if (reason.trim() === '') return;
    }
    close.mutate(
      { sessionId, input: force ? { force: true, reason: reason.trim() } : {} },
      { onSuccess: onClosed },
    );
  }

  return (
    <Sheet open onClose={onClose} title="Close this session?">
      <div className="flex flex-col gap-4">
        <p className="text-base text-ink">{where}</p>
        {bill && (
          <dl className={cn(cardClasses({ padding: 'none' }), 'px-4 py-2 text-sm')}>
            <div className="flex items-baseline justify-between gap-3 py-1.5">
              <dt className="text-muted">Total</dt>
              <dd className="font-medium text-ink tabular-nums">
                <Money value={asMoney(bill.total_amount)} />
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-3 py-1.5">
              <dt className="text-muted">Paid</dt>
              <dd className="font-medium text-ink tabular-nums">
                <Money value={asMoney(bill.paid_amount)} />
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-3 py-1.5">
              <dt className="font-semibold text-ink">Balance due</dt>
              <dd className="font-semibold text-ink tabular-nums">
                <Money value={asMoney(bill.remaining_balance)} />
              </dd>
            </div>
          </dl>
        )}
        {pending > 0 && (
          <Notice tone="warn">
            An online payment was not completed. It does not reduce the balance due, and closing the
            session cancels it.
          </Notice>
        )}
        <p className="text-sm text-muted">
          A session can only be closed when the bill is settled and everything is served.
        </p>
        {error && (
          <p role="alert" className="text-sm font-medium text-danger">
            {describeCounterError(error, 'close', fmt)}
          </p>
        )}
        {overridable && (
          <div className="flex flex-col gap-3 rounded-surface border border-attention-line bg-attention-soft p-3">
            <p className="text-sm font-semibold text-attention-ink">
              Close anyway (branch manager)
            </p>
            <Input
              label="Reason for closing anyway"
              value={reason}
              maxLength={255}
              onChange={(event) => setReason(event.target.value)}
              error={attempted && reason.trim() === '' ? 'Give a reason.' : undefined}
              autoComplete="off"
            />
            <Button
              variant="destructive"
              size="lg"
              loading={close.isPending}
              onClick={() => run(true)}
            >
              Close anyway
            </Button>
          </div>
        )}
        {/* After a refusal a manager can override, the refused "Close session" steps back: the amber box is
            the one way forward, and a plain retry stays available without competing with it. */}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={overridable ? 'secondary' : 'primary'}
            loading={close.isPending}
            onClick={() => run(false)}
          >
            Close session
          </Button>
        </div>
      </div>
    </Sheet>
  );
}

// ─── Open a session (branch manager) ─────────────────────────────────────────

export function OpenSessionSheet({
  location,
  onOpened,
  onClose,
}: {
  location: FloorLocation;
  onOpened: (sessionId: string) => void;
  onClose: () => void;
}) {
  const open = useOpenSession();
  const intent = useIntentKey();
  const isSeat = location.location_type === 'SEAT';
  const [name, setName] = useState('');
  const [guests, setGuests] = useState('1');
  const [attempted, setAttempted] = useState(false);
  const where = locationText(location.location_type, location.location_label);

  const size = isSeat ? 1 : Number(guests);
  const nameError = attempted && name.trim() === '' ? 'Enter a name.' : undefined;
  const guestsError =
    attempted && !isSeat && !(Number.isInteger(size) && size >= 1)
      ? 'Enter the number of guests.'
      : undefined;

  function submit(event: FormEvent) {
    event.preventDefault();
    setAttempted(true);
    const display = name.trim();
    if (display === '' || !(Number.isInteger(size) && size >= 1) || open.isPending) return;
    const input = { location_id: location.location_id, display_name: display, party_size: size };
    open.mutate(
      { input, idempotencyKey: intent.keyFor(JSON.stringify(input)) },
      {
        onSuccess: (result) => {
          intent.reset();
          onOpened(result.session_id);
        },
      },
    );
  }

  return (
    <Sheet open onClose={onClose} title={`Open ${where}`}>
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Input
          label="Guest name"
          value={name}
          maxLength={100}
          onChange={(event) => setName(event.target.value)}
          error={nameError}
          autoComplete="off"
        />
        {!isSeat && (
          <Input
            label="Number of guests"
            type="number"
            inputMode="numeric"
            min={1}
            value={guests}
            onChange={(event) => setGuests(event.target.value)}
            error={guestsError}
          />
        )}
        {open.error && (
          <p role="alert" className="text-sm font-medium text-danger">
            {describeCounterError(open.error, 'open')}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={open.isPending}>
            Open session
          </Button>
        </div>
      </form>
    </Sheet>
  );
}
